import { areObjectIdsEqual } from 'helpers/idComparison';
import { pinnedVersionOf } from '../../slices/gallery/model';
import {
  DocumentationNavigationFile,
  FileOpenContext,
  GalleryDisplaySnapshot,
  GalleryUrlState,
} from './types';

export function findMatchingFileIndex(
  files: DocumentationNavigationFile[],
  openRequest: FileOpenContext
): number {
  const fileIndex = files.findIndex(
    (file) => file.documentationId === openRequest.documentationId
  );

  if (fileIndex === -1) {
    return -1;
  }

  const matchingFile = files[fileIndex];
  if (!matchingFile) {
    return -1;
  }

  const deletedMatches = Boolean(matchingFile.deleted) === openRequest.isArchived;
  return deletedMatches ? fileIndex : -1;
}

export function shouldDelayMissingDecision(
  openRequest: FileOpenContext,
  hasObservedLoading: boolean,
  hasObservedFilesChange: boolean
): boolean {
  return (
    openRequest.hasNavigatedToParentDirectory &&
    !hasObservedLoading &&
    !hasObservedFilesChange
  );
}

/**
 * Documentation shown in the open preview, or `undefined` when it is closed.
 */
export function getDisplayedDocumentationId(
  state: GalleryDisplaySnapshot
): string | undefined {
  return state.galleryOpen
    ? state.files[state.currentSlide]?.documentationId
    : undefined;
}

/**
 * The version named in the URL: only a version the user opened by a link, and only while the
 * preview shows it. A link to a version the documentation does not have shows the current one,
 * so the URL drops it.
 */
export function getDisplayedVersionId(state: GalleryDisplaySnapshot): string | undefined {
  if (!state.galleryOpen) {
    return undefined;
  }
  const displayedFile = state.files[state.currentSlide];
  const pinnedVersionId = pinnedVersionOf(state.pinnedVersion, displayedFile?.documentationId);
  return pinnedVersionId && areObjectIdsEqual(displayedFile?.versionId, pinnedVersionId) ? pinnedVersionId : undefined;
}

/** Selector for the URL-relevant preview state (compare with `shallow`). */
export function selectGalleryUrlState(state: GalleryDisplaySnapshot): GalleryUrlState {
  return {
    galleryOpen: state.galleryOpen,
    displayedDocumentationId: getDisplayedDocumentationId(state),
    displayedVersionId: getDisplayedVersionId(state),
  };
}
